import { KeyboardEvent, useCallback, useState } from 'react';
import { ComponentProps } from '@app/participant-fields';
import { Criterion } from 'queries';
import { setCriterionValues } from 'lib/criteria';
import { CreatableAutoComplete } from '@app/form';
import { ActionMeta, MultiValueProps, ValueType } from 'react-select';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { Chip } from '@app/core';
import classnames from 'classnames';

interface OptionType {
  value: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    margin: theme.marginBase / 4,
    padding: 0,
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    width: 'fit-content',
    alignItems: 'stretch',

    '& p:first-letter': {
      textTransform: 'capitalize',
    },
  },
  text: {
    lineHeight: '150%',
    padding: [
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase,
    ],
  },
  removeIcon: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.marginBase / 2,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
}));

const MultiValue = (props: MultiValueProps<{ value: string }>) => {
  const tag = props.data.value;
  const classes = useStyles();
  return (
    <Chip
      color="green"
      className={classes.chip}
      {...props.innerProps}
      key={tag}
    >
      <p className={classes.text}>{tag}</p>
      <div className={classes.removeIcon} {...props.removeProps}>
        <i className={classnames('ri-close-line', classes.icon)} />
      </div>
    </Chip>
  );
};
const components = {
  MultiValue,
  Menu: () => null,
  DropdownIndicator: () => null,
};

export const CriterionText = ({
  field,
  onChange,
  value: criterion,
  allowedFields,
  criteria,
  ...props
}: ComponentProps<Criterion>) => {
  const tags = (criterion?.allowList as string[]) || [];
  const [inputValue, setInputValue] = useState('');

  const _onChange = useCallback(
    (value: ValueType<OptionType, true>, action?: ActionMeta<OptionType>) => {
      const removedValue =
        action?.action === 'remove-value' && action?.removedValue?.value;
      const newValue =
        (removedValue
          ? tags.filter((val) => removedValue !== val)
          : value.map(({ value }) => value)) || [];

      if (onChange) {
        onChange(setCriterionValues(criterion, newValue));
      }
      return;
    },
    [tags, onChange],
  );

  const saveAndResetInputText = (event: KeyboardEvent<HTMLElement>) => {
    event.preventDefault();
    if (!tags.includes(inputValue)) {
      onChange(setCriterionValues(criterion, [...tags, inputValue]));
    }
    setInputValue('');
    return;
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!inputValue) return;
      switch (event.key) {
        case 'Enter':
          return saveAndResetInputText(event);
        case ';':
          return saveAndResetInputText(event);
        case ',':
          return saveAndResetInputText(event);
        case 'Tab':
          return saveAndResetInputText(event);
        default:
          return;
      }
    },
    [onChange, setInputValue, inputValue, tags],
  );

  const current: OptionType[] = tags.map((value) => ({ value }));

  return (
    <CreatableAutoComplete<OptionType, true>
      {...props}
      options={[]}
      small={false}
      value={current}
      isMulti
      label={field.label}
      placeholder={field.placeholder}
      secondaryLabel={field.criterion?.description || ''}
      components={components as any}
      onChange={_onChange}
      inputValue={inputValue}
      onKeyDown={handleKeyDown}
      onInputChange={setInputValue}
    />
  );
};
